'use client';

import Script from 'next/script';
import { memo, type FC, type PropsWithChildren } from 'react';
import { useExternalScript } from './internal/useExternalScript';
import type { IScriptLoaderProps } from './IScriptLoader';

/**
 * A component to load external scripts into the document. It should agnostically
 * handle loading scripts via Next.js `<Script>` component or via a standard DOM approach.
 *
 * Based on the sent `method` prop, it will decide which approach to use.
 *
 * @param props - The properties for the script loader component.
 * @returns The script loader component, which may render:
 * - The children directly (in case of `external` method), loading the script via DOM.
 * - A Next.js `<Script>` component (in case of `next` method), which will handle the script loading internally.
 */
const ScriptLoader: FC<PropsWithChildren<IScriptLoaderProps>> = memo(
  ({
    id,
    method = 'next',
    shouldRemoveOnUnmount = true,
    onReady,
    strategy,
    children,
    ...commonProps
  }) => {
    /**
     * Use the external script loader hook to load the script via DOM.
     *
     * It will only execute the logic if the method is 'external'.
     * @see {@link useExternalScript} for more details.
     */
    useExternalScript({ id, method, shouldRemoveOnUnmount, ...commonProps });

    // If the method is 'next', use Next.js Script component to load the script.
    if (method === 'next') {
      return (
        <Script id={id} strategy={strategy} onReady={onReady} {...commonProps}>
          {children}
        </Script>
      );
    }

    // Otherwise, just render the children directly, as the script is already being loaded via DOM.
    return <>{children}</>;
  }
);

export default ScriptLoader;
